export interface Projection {
  value: number;
  daysCovered: number;
  daysInMonth: number;
  monthEnd: Date;
  /** Minutes of today counted (Berlin wall clock); null when today's data is not included. */
  todayMinutes: number | null;
}

/** A day always counts as 1440 minutes, daylight-saving switch days included. */
export const MINUTES_PER_DAY = 1440;

/** Below this many days covered (the 1st before 12:00 Berlin) a straight-line figure is too noisy to show. */
export const MIN_DAYS_COVERED = 0.5;

const berlinParts = (now: Date): { year: number; month: number; day: number; hour: number; minute: number } => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour"), minute: get("minute") };
};

/** Today's day of the month on the Berlin calendar. */
export function berlinDayOfMonth(now: Date): number {
  return berlinParts(now).day;
}

/** Minutes since midnight on the Berlin wall clock (0–1439). */
export function berlinMinutesOfDay(now: Date): number {
  const { hour, minute } = berlinParts(now);
  return hour * 60 + minute;
}

/**
 * Days of the Berlin month covered by the figures at `now`: the completed days, plus the elapsed part of
 * today when today's sessions are included (minutes since Berlin midnight ÷ 1440).
 */
export function daysCoveredAt(now: Date, liveDayIncluded: boolean): number {
  const completed = berlinDayOfMonth(now) - 1;
  return liveDayIncluded ? completed + berlinMinutesOfDay(now) / MINUTES_PER_DAY : completed;
}

/** The Berlin calendar month of `now` as "YYYY-MM". */
export function berlinYearMonth(now: Date): string {
  const { year, month } = berlinParts(now);
  return `${year}-${String(month).padStart(2, "0")}`;
}

const MONTH_ABBREVIATIONS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * The point in the Berlin month the figures reach at `now`, for "last month at the same point":
 * "3 Nov, 15:10" when today is included, "end of 2 Nov" when they cover completed days only.
 */
export function berlinSamePointLabel(now: Date, liveDayIncluded: boolean): string {
  const { month, day, hour, minute } = berlinParts(now);
  const name = MONTH_ABBREVIATIONS[month - 1];
  return liveDayIncluded
    ? `${day} ${name}, ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
    : `end of ${day - 1} ${name}`;
}

/** Number of days in a "YYYY-MM" month. */
export function daysInMonth(yearMonth: string): number {
  const [year, month] = yearMonth.split("-").map(Number);
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Linear month-end projection: value ÷ daysCovered × daysInMonth.
 * Null when the value is missing, fewer than {@link MIN_DAYS_COVERED} days are covered, or `yearMonth` is
 * not the Berlin month of `now`.
 */
export function projectToMonthEnd(
  value: number | null | undefined,
  now: Date,
  yearMonth: string,
  liveDayIncluded: boolean,
): Projection | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  if (yearMonth !== berlinYearMonth(now)) return null;

  const daysCovered = daysCoveredAt(now, liveDayIncluded);
  if (daysCovered < MIN_DAYS_COVERED) return null;

  const days = daysInMonth(yearMonth);
  const [year, month] = yearMonth.split("-").map(Number);
  return {
    value: (value / daysCovered) * days,
    daysCovered,
    daysInMonth: days,
    monthEnd: new Date(Date.UTC(year, month - 1, days)),
    todayMinutes: liveDayIncluded ? berlinMinutesOfDay(now) : null,
  };
}
